import React, { useCallback, useMemo, useRef } from 'react';

import type { Dispatch, MutableRefObject, Ref, SetStateAction } from 'react';

import { Bed } from '@travel/icons/service';
import { List } from '@travel/icons/ui';
import { useTranslation } from '@travel/traveler-core/hooks';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import TabButton from '@travel/ui/components/NavigationTab/TabButton';

import type { ProviderInfoListTypes } from 'ProviderInformation-Types';

import styles from './listingTypeSelectorTabList.module.scss';

type Props = {
  /** state to capture current active tab */
  activeListType: ProviderInfoListTypes;

  /** function to set activeListType state */
  setActiveListType: Dispatch<SetStateAction<ProviderInfoListTypes>>;

  /** ref to capture previously selected tab */
  prevActiveListTypeRef: MutableRefObject<ProviderInfoListTypes>;

  /** function to set isAutoOpenDatePicker state */
  setIsAutoOpenDatePicker: Dispatch<SetStateAction<boolean>>;
  innerRef: Ref<HTMLDivElement>;

  /** callback function to be called when tab is changed */
  onChangeListType: (activeListType: string) => void;
};

/**
 * tab list wrapper that houses room view and plan view tab buttons
 * is sent as a prop to SearchForm which then renders this inside it
 */
function ListingTypeSelectorTabList(props: Props) {
  const {
    activeListType,
    setActiveListType,
    prevActiveListTypeRef,
    setIsAutoOpenDatePicker,
    innerRef,
    onChangeListType,
  } = props;

  // Labels
  const roomListLabel = useTranslation({ id: 'Provider_Info.Navigation.Room_View' });
  const planListLabel = useTranslation({ id: 'Provider_Info.Navigation.Plan_View' });

  // listing type selector tab titles including icons
  const roomListTitle = useMemo(() => {
    const isRoomListSelected = activeListType === PROVIDER_LIST_TYPE.ROOM_LIST;

    return (
      <span className={styles.tabListTitleWrapper}>
        <Bed size={16} color={isRoomListSelected ? 'white' : 'cilantro'} /> {roomListLabel}
      </span>
    );
  }, [roomListLabel, activeListType]);

  const planListTitle = useMemo(() => {
    const isPlanListSelected = activeListType === PROVIDER_LIST_TYPE.PLAN_LIST;

    return (
      <span className={styles.tabListTitleWrapper}>
        <List size={16} color={isPlanListSelected ? 'white' : 'cilantro'} /> {planListLabel}
      </span>
    );
  }, [planListLabel, activeListType]);

  const tabs = useMemo(
    () => [
      {
        id: PROVIDER_LIST_TYPE.ROOM_LIST,
        title: roomListTitle,
        content: null,
      },
      {
        id: PROVIDER_LIST_TYPE.PLAN_LIST,
        title: planListTitle,
        content: null,
      },
    ],
    [planListTitle, roomListTitle],
  );

  const handleChangeTabs = useCallback(
    (id: string) => {
      prevActiveListTypeRef.current = activeListType;
      // to re-calculated the text ellipsis when the specific tab is displayed
      setActiveListType(id as ProviderInfoListTypes);

      onChangeListType(id);
    },
    [activeListType, onChangeListType, prevActiveListTypeRef, setActiveListType],
  );

  const onClickTab = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      handleChangeTabs(event.currentTarget.id);
      setIsAutoOpenDatePicker(false);
    },
    [handleChangeTabs, setIsAutoOpenDatePicker],
  );

  const onKeyPressed = useCallback(
    (newTabId?: string) => {
      if (newTabId) {
        handleChangeTabs(newTabId);
        tabRefs.current[newTabId]?.focus();
      }
    },
    [handleChangeTabs],
  );

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  return (
    <div
      ref={innerRef}
      role="tablist"
      data-testid="listingTypeSelectorTabList-wrapper"
      aria-label=""
      className={styles.tabMenu}
    >
      {tabs.map((tab, index) => {
        const isSelected = activeListType === tab.id;

        return (
          <TabButton
            key={tab.id}
            isSelected={isSelected}
            tabs={tabs}
            tab={tab}
            selectedTabIndex={index}
            onClickTab={onClickTab}
            onKeyPressed={onKeyPressed}
            tabButtonClassName={styles.tabButton}
            ref={(ref) => (tabRefs.current[tab.id] = ref)}
          />
        );
      })}
    </div>
  );
}

export default React.memo(ListingTypeSelectorTabList);
